'use client';

import { c as _c } from "react/compiler-runtime";
import { Flag } from '@7f-ui/components/flag/flag';
import { RichText } from '@7f-ui/components/rich-text/rich-text';
import { StarRating } from '@7f-ui/components/star-rating/star-rating';
import { TextExpander } from '@7f-ui/components/text-expander/text-expander';
import { AccommodationFlag, AccommodationRatingFlag, AccommodationRecommendedFlag } from '@feat/feat-accommodation/badges';
import { shopTestId } from '@utils/utils-shop-test-ids';
import type { AccommodationFragment } from 'src/lib/backend/documents/accommodation';
import { sanitizeBackendHtml } from '@/lib/security/sanitize-html';
import { useAppAccommodationSearch } from '../_hooks/use-app-accommodation-search';
type AppAccommodationHeaderProps = {
  accommodation: AccommodationFragment;
  onLinkClick?: () => void;
};
export function AppAccommodationHeader(t0) {
  const $ = _c(46);
  const {
    accommodation,
    onLinkClick
  } = t0;
  const {
    data: accommodationSearch
  } = useAppAccommodationSearch(accommodation.id);
  const starCount = accommodation.stars ?? 0;
  const showStars = !!accommodation.stars;
  const showDeal = accommodationSearch?.data[0]?.pricing?.tags?.[0];
  const showStarsOrDeal = showStars || showDeal;
  let t1;
  if ($[0] !== accommodationSearch?.data || $[1] !== showStars || $[2] !== showStarsOrDeal || $[3] !== starCount) {
    t1 = showStarsOrDeal && <div className="flex items-center gap-2">{showStars ? <StarRating stars={starCount} label={`${starCount} ${starCount === 1 ? "Stern" : "Sterne"} Unterkunft`} /> : null}<div className="sm:hidden"><AccommodationFlag dealTag={accommodationSearch?.data[0]?.pricing?.tags?.[0]} /></div></div>;
    $[0] = accommodationSearch?.data;
    $[1] = showStars;
    $[2] = showStarsOrDeal;
    $[3] = starCount;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = shopTestId("MainSection.title");
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== accommodation.name) {
    t3 = <h1 className="font-heading1" itemProp="name" data-testid={t2}>{accommodation.name}</h1>;
    $[6] = accommodation.name;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== accommodation.city) {
    t4 = <p>{accommodation.city}</p>;
    $[8] = accommodation.city;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <div className="grid gap-2">{t3}{t4}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== accommodation.tags) {
    t6 = accommodation.tags.map(_temp);
    $[13] = accommodation.tags;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t6) {
    t7 = <div className="flex flex-wrap gap-2">{t6}</div>;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t1 || $[18] !== t5 || $[19] !== t7) {
    t8 = <div className="flex flex-col gap-2 sm:gap-4">{t1}{t5}{t7}</div>;
    $[17] = t1;
    $[18] = t5;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== accommodation.isRecommended) {
    t9 = accommodation.isRecommended && <AccommodationRecommendedFlag />;
    $[21] = accommodation.isRecommended;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== accommodation.isRecommended || $[24] !== accommodation.numberOfReviews || $[25] !== accommodation.reviewScore || $[26] !== accommodation.reviewScores || $[27] !== accommodation.reviews || $[28] !== onLinkClick) {
    t10 = !accommodation.isRecommended && <AccommodationRatingFlag reviews={accommodation.reviews} reviewScore={accommodation.reviewScore} reviewScores={accommodation.reviewScores} numberOfReviews={accommodation.numberOfReviews} onLinkClick={onLinkClick} />;
    $[23] = accommodation.isRecommended;
    $[24] = accommodation.numberOfReviews;
    $[25] = accommodation.reviewScore;
    $[26] = accommodation.reviewScores;
    $[27] = accommodation.reviews;
    $[28] = onLinkClick;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t10 || $[31] !== t8 || $[32] !== t9) {
    t11 = <div className="flex flex-col gap-4">{t8}{t9}{t10}</div>;
    $[30] = t10;
    $[31] = t8;
    $[32] = t9;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== accommodation.description) {
    t12 = sanitizeBackendHtml(accommodation.description);
    $[34] = accommodation.description;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t12) {
    t13 = <RichText text={t12} />;
    $[36] = t12;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== accommodation.additionalDescription) {
    t14 = accommodation.additionalDescription && <TextExpander><RichText text={sanitizeBackendHtml(accommodation.additionalDescription)} /></TextExpander>;
    $[38] = accommodation.additionalDescription;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== t13 || $[41] !== t14) {
    t15 = <div className="flex flex-col gap-2">{t13}{t14}</div>;
    $[40] = t13;
    $[41] = t14;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t11 || $[44] !== t15) {
    t16 = <div className="flex flex-col gap-5 sm:gap-8">{t11}{t15}</div>;
    $[43] = t11;
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  return t16;
}
function _temp(tag) {
  return <Flag key={tag}>{tag}</Flag>;
}